/**
 * JobSearchFilters Component
 * 
 * Left sidebar filter component for the job search results page (/darbo-skelbimai).
 * Displays collapsible filter sections with modal-based selections.
 * 
 * @usage Rendered in JobSearchPage.tsx as a left sidebar (280px width)
 * @note NOT to be confused with HomeDetailedSearch (used on homepage)
 * 
 * Features:
 * - Collapsible filter sections (Darbo vietos, Darbo sritys, etc.)
 * - Modal-based detailed selections
 * - Sticky positioning for better UX
 * - Clear all filters functionality
 * - Live result count preview
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MapPin, Briefcase, Euro, Building, Clock, Calendar, Settings, Download, Mail, X, CheckCircle, AlertCircle, Trash2, ChevronLeft, SlidersHorizontal, Save, PlusCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { addLocalSavedSearch, addLocalSubscription } from '../../services/localSavedSearches';
import { LITHUANIAN_CITIES, splitMajorCities } from '../../constants/cities';
import FilterModal from './FilterModal';
import api from '../../services/api';
import './JobSearchFilters.scss';

interface ToastState {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface FilterOptions {
  searchKeyword: string;
  keywords: string[];
  location: string[];
  workField: string[];
  positionType: string[];
  positions: string[];
  fieldIds: number[];
  tipasIds: number[];
  positionIds: number[];
  companyActivity: string;
  companyName: string;
  companyNames: string[];
  employerId?: number;
  jobType: string[];
  workTime: string[];
  salaryMin: string;
  postedWithin: string;
  language: string[];
  directEmployersOnly: boolean;
  hideApplied: boolean;
  hideMarked: boolean;
  hideWithoutSalary: boolean;
}

interface JobSearchFiltersProps {
  isVisible: boolean;
  onFiltersChange: (filters: FilterOptions) => void;
  onFiltersPreview?: (filters: FilterOptions) => void;
  onCollapse?: () => void;
  currentFilters: FilterOptions;
  liveCount?: number | null;
  countLoading?: boolean;
  facets?: Record<string, Record<string, number>>;
}

interface CVTemplate {
  id: number;
  category: string;
  value: string;
  label: string;
  parent_value?: string | null;
}

const JobSearchFilters: React.FC<JobSearchFiltersProps> = ({
  isVisible,
  onFiltersChange,
  onFiltersPreview,
  onCollapse,
  currentFilters,
  liveCount,
  countLoading,
  facets = {}
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [saveSearchTitle, setSaveSearchTitle] = useState('');
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeFrequency, setSubscribeFrequency] = useState<'daily' | 'weekly'>('daily');
  const [subscribeName, setSubscribeName] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState<ToastState>({ show: false, message: '', type: 'info' });
  
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };
  
  // Staged filters - changes are accumulated here before being applied
  const [stagedFilters, setStagedFilters] = useState<FilterOptions>(currentFilters);
  
  
  // Sync staged filters when currentFilters change (e.g., from URL params)
  useEffect(() => {
    setStagedFilters(currentFilters);
    setSalarySlider(currentFilters.salaryMin ? parseInt(currentFilters.salaryMin, 10) || 0 : 0);
  }, [currentFilters]);

  const [cvTemplates, setCvTemplates] = useState<{
    work_field?: CVTemplate[];
    work_type?: CVTemplate[];
    work_time?: CVTemplate[];
    position_type?: CVTemplate[];
    work_location?: CVTemplate[];
    position?: CVTemplate[];
    job_language?: CVTemplate[];
    company_activity?: CVTemplate[];
  }>({});

  const [locationSearch, setLocationSearch] = useState('');
  const [companies, setCompanies] = useState<{ id: number; company_name: string; job_count: number; company_activity?: string }[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [salarySlider, setSalarySlider] = useState(0);
  const salaryDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Relational taxonomy for the "Darbo srritys" cascade.
  const [taxFields, setTaxFields] = useState<{ id: number; name: string }[]>([]);
  const [taxTipai, setTaxTipai] = useState<{ id: number; name: string }[]>([]);
  const [positionsByField, setPositionsByField] = useState<Record<number, { id: number; name: string; tipas_id?: number | null }[]>>({});
  // Names for selected position ids whose field hasn't been expanded, so chips
  // show the position name instead of "#id" after remount/navigation.
  const [positionNames, setPositionNames] = useState<Record<number, string>>({});
  const [taxFieldSearch, setTaxFieldSearch] = useState('');
  const [taxPosSearch, setTaxPosSearch] = useState('');
  // Relational job counts per field/type/position id.
  const [taxFacets, setTaxFacets] = useState<{ fields: Record<number, number>; tipai: Record<number, number>; positions: Record<number, number> }>({ fields: {}, tipai: {}, positions: {} });
  const [taxFacetsReady, setTaxFacetsReady] = useState(false);

  const hasFacets = Object.keys(facets).length > 0;

  // Refresh relational cascade counts when the "Darbo srritys" modal is open and
  // the staged taxonomy selection (or key filters) changes.
  useEffect(() => {
    if (openModal !== 'workField') return;
    // Don't reset taxFacetsReady here — keep the previous counts/blur visible
    // during the refetch so options don't briefly all light up (flicker).
    const t = setTimeout(() => fetchTaxFacets(stagedFilters), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, stagedFilters.fieldIds, stagedFilters.tipasIds, stagedFilters.positionIds, stagedFilters.location, stagedFilters.jobType, stagedFilters.workTime, stagedFilters.salaryMin]);

  // Load CV templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_URL}/cv-templates?grouped=true`);
        if (response.ok) {
          const result = await response.json();
          setCvTemplates(result.data || {});
        }
      } catch (error) {
        console.error('Error loading CV templates:', error);
      }
    };
    loadTemplates();
  }, []);

  // Load relational taxonomy (work fields + position types).
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/taxonomy');
        setTaxFields(res.data?.data?.darboSritys || []);
        setTaxTipai(res.data?.data?.tipai || []);
      } catch (error) {
        console.error('Error loading taxonomy:', error);
      }
    })();
  }, []);

  // Resolve names for any selected position ids we don't yet know (e.g. restored
  // from the URL), so the sidebar chips read as names rather than "#id".
  useEffect(() => {
    const known = (id: number) => positionNames[id] || Object.values(positionsByField).some(arr => arr.some(p => p.id === id));
    const missing = stagedFilters.positionIds.filter(id => !known(id));
    if (missing.length === 0) return;
    missing.forEach(async id => {
      try {
        const res = await api.get(`/taxonomy/positions/${id}`);
        const name = res.data?.data?.name;
        if (name) setPositionNames(prev => ({ ...prev, [id]: name }));
      } catch { /* leave as #id */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedFilters.positionIds]);

  const ensurePositionsForField = useCallback(async (fieldId: number) => {
    setPositionsByField(prev => {
      if (prev[fieldId]) return prev;
      api.get(`/taxonomy/fields/${fieldId}/positions`)
        .then(res => setPositionsByField(p => ({ ...p, [fieldId]: (res.data?.data || []).map((x: any) => ({ id: x.id, name: x.name, tipas_id: x.tipas_id != null ? Number(x.tipas_id) : null })) })))
        .catch(() => {});
      return prev;
    });
  }, []);

  // Fetch relational job counts for the cascade whenever the staged taxonomy
  // selection (or a few key filters) changes.
  const fetchTaxFacets = useCallback(async (sf: FilterOptions) => {
    try {
      const params = new URLSearchParams();
      if (sf.fieldIds.length) params.set('fieldIds', sf.fieldIds.join(','));
      if (sf.tipasIds.length) params.set('tipasIds', sf.tipasIds.join(','));
      if (sf.positionIds.length) params.set('positionIds', sf.positionIds.join(','));
      if (sf.location.length) params.set('location', sf.location.join(','));
      if (sf.jobType.length) params.set('jobType', sf.jobType.join(','));
      if (sf.workTime.length) params.set('workTime', sf.workTime.join(','));
      if (sf.salaryMin) params.set('salaryMin', sf.salaryMin);
      const res = await api.get(`/jobs/search/taxonomy-facets?${params.toString()}`);
      setTaxFacets(res.data?.data || { fields: {}, tipai: {}, positions: {} });
      setTaxFacetsReady(true);
    } catch {
      setTaxFacetsReady(true);
    }
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_URL}/jobs/companies`);
        if (response.ok) {
          const result = await response.json();
          setCompanies(result.data?.companies || []);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    };
    loadCompanies();
  }, []);

  const openFilterModal = (modalName: string) => {
    setOpenModal(modalName);
  };

  const closeModal = () => {
    setOpenModal(null);
  };

  // Update filters and apply immediately
  const updateStagedFilters = useCallback((newFilters: FilterOptions) => {
    setStagedFilters(newFilters);
    onFiltersChange(newFilters);
  }, [onFiltersChange]);

  const handleArrayCheckbox = (field: 'location' | 'workField' | 'positionType' | 'positions' | 'jobType' | 'workTime' | 'language' | 'companyNames', value: string) => {
    const currentValues = stagedFilters[field];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    updateStagedFilters({
      ...stagedFilters,
      [field]: newValues
    });
  };

  const handleInputChange = (field: keyof FilterOptions, value: string) => {
    updateStagedFilters({
      ...stagedFilters,
      [field]: value
    });
  };

  // Handle adding keywords (create chips on Space or Enter)

  // Remove a specific keyword chip
  const removeKeyword = (keyword: string) => {
    updateStagedFilters({
      ...stagedFilters,
      keywords: stagedFilters.keywords.filter(k => k !== keyword)
    });
  };

  const applyFilters = useCallback(() => {
    onFiltersChange(stagedFilters);
  }, [stagedFilters, onFiltersChange]);

  const clearAllFilters = () => {
    const emptyFilters: FilterOptions = {
      searchKeyword: '',
      keywords: [],
      location: [],
      workField: [],
      positionType: [],
      positions: [],
      fieldIds: [],
      tipasIds: [],
      positionIds: [],
      companyActivity: '',
      companyName: '',
      companyNames: [],
      employerId: undefined,
      jobType: [],
      workTime: [],
      salaryMin: '',
      postedWithin: '',
      language: [],
      directEmployersOnly: false,
      hideApplied: false,
      hideMarked: false,
      hideWithoutSalary: false
    };
    setStagedFilters(emptyFilters);
    onFiltersChange(emptyFilters);
    setSalarySlider(0);
    setLocationSearch('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (stagedFilters.keywords.length > 0) count += stagedFilters.keywords.length;
    if (stagedFilters.location.length > 0) count += stagedFilters.location.length;
    if (stagedFilters.workField.length > 0) count += stagedFilters.workField.length;
    if (stagedFilters.jobType.length > 0) count += stagedFilters.jobType.length;
    if (stagedFilters.workTime.length > 0) count += stagedFilters.workTime.length;
    if (stagedFilters.language.length > 0) count += stagedFilters.language.length;
    if (stagedFilters.fieldIds.length > 0) count += stagedFilters.fieldIds.length;
    if (stagedFilters.tipasIds.length > 0) count += stagedFilters.tipasIds.length;
    if (stagedFilters.positionIds.length > 0) count += stagedFilters.positionIds.length;
    if (stagedFilters.companyNames.length > 0) count += stagedFilters.companyNames.length;
    if (stagedFilters.salaryMin) count++;
    if (stagedFilters.companyActivity) count++;
    if (stagedFilters.directEmployersOnly) count++;
    if (stagedFilters.postedWithin) count++;
    return count;
  };

  const removeFilter = (type: 'location' | 'workField' | 'jobType' | 'workTime' | 'language' | 'positionType' | 'positions' | 'companyNames', value: string) => {
    handleArrayCheckbox(type, value);
  };

  const buildSearchTitle = () => {
    const allKeywords = [
      ...(currentFilters.keywords || []),
      ...(currentFilters.searchKeyword ? [currentFilters.searchKeyword] : [])
    ].filter(Boolean);
    const filterParts: string[] = [];
    if (allKeywords.length > 0) filterParts.push(allKeywords.join(', '));
    if (currentFilters.location?.length) filterParts.push(currentFilters.location.join(', '));
    if (currentFilters.workField?.length) filterParts.push(currentFilters.workField.join(', '));
    return filterParts.length > 0 
      ? filterParts.join(' - ').substring(0, 100)
      : `Paieška ${new Date().toLocaleDateString('lt-LT')}`;
  };

  const handleSaveSearch = () => {
    setSaveSearchTitle(buildSearchTitle());
    setShowSavePanel(true);
  };

  const confirmSaveSearch = async () => {
    const title = saveSearchTitle.trim() || buildSearchTitle();
    const allKeywords = [
      ...(currentFilters.keywords || []),
      ...(currentFilters.searchKeyword ? [currentFilters.searchKeyword] : [])
    ].filter(Boolean);

    if (!isAuthenticated) {
      addLocalSavedSearch({
        name: title,
        keywords: allKeywords,
        location: currentFilters.location || [],
        work_field: currentFilters.workField || [],
        job_type: currentFilters.jobType || [],
        work_time: currentFilters.workTime || [],
        salary_min: currentFilters.salaryMin ? parseFloat(currentFilters.salaryMin) : null,
      });
      showToast('Paieška išsaugota!', 'success');
      setShowSavePanel(false);
      setTimeout(() => navigate('/issaugotos-paieskos'), 800);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const keywordsString = allKeywords.length > 0 ? allKeywords.join(',') : null;

      const response = await fetch(`${API_URL}/saved-searches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title,
          keywords: keywordsString,
          location: currentFilters.location || [],
          work_field: currentFilters.workField || [],
          job_type: currentFilters.jobType || [],
          work_time: currentFilters.workTime || [],
          salary_min: currentFilters.salaryMin || null,
          company_activity: currentFilters.companyActivity || null,
          is_subscribed: true
        })
      });

      if (response.ok) {
        showToast('Paieška išsaugota sėkmingai!', 'success');
        setShowSavePanel(false);
        setTimeout(() => navigate('/issaugotos-paieskos'), 800);
      } else {
        showToast('Nepavyko išsaugoti paieškos', 'error');
      }
    } catch (error) {
      console.error('Error saving search:', error);
      showToast('Įvyko klaida išsaugant paiešką', 'error');
    }
  };

  const handleSubscribe = () => {
    setSubscribeName(buildSearchTitle());
    setShowSubscribeModal(true);
  };

  const handleSubscribeSubmit = async () => {
    if (!subscribeName.trim()) {
      showToast('Prašome įvesti paieškos pavadinimą', 'error');
      return;
    }

    setSubscribing(true);
    const allKeywords = [
      ...(currentFilters.keywords || []),
      ...(currentFilters.searchKeyword ? [currentFilters.searchKeyword] : [])
    ].filter(Boolean);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      if (!isAuthenticated) {
        // Guest: POST to guest subscription endpoint + localStorage
        const res = await fetch(`${API_URL}/subscriptions/guest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: subscribeEmail.trim(),
            name: subscribeName.trim(),
            filters: {
              keywords: allKeywords,
              location: currentFilters.location || [],
              work_field: currentFilters.workField || [],
              job_type: currentFilters.jobType || [],
              work_time: currentFilters.workTime || [],
              salary_min: currentFilters.salaryMin ? parseFloat(currentFilters.salaryMin) : null,
            },
            frequency: subscribeFrequency,
          }),
        });

        if (res.ok) {
          addLocalSubscription({
            name: subscribeName.trim(),
            email: subscribeEmail.trim(),
            frequency: subscribeFrequency,
            keywords: allKeywords,
            location: currentFilters.location || [],
            work_field: currentFilters.workField || [],
            job_type: currentFilters.jobType || [],
            work_time: currentFilters.workTime || [],
            salary_min: currentFilters.salaryMin ? parseFloat(currentFilters.salaryMin) : null,
            enabled: true,
          });
          setSubscribeSuccess(true);
          setTimeout(() => { setShowSubscribeModal(false); setSubscribeSuccess(false); }, 2000);
        } else {
          showToast('Nepavyko sukurti prenumeratos', 'error');
        }
      } else {
        // Authenticated: save as subscribed search
        const token = localStorage.getItem('auth_token');
        const keywordsString = allKeywords.length > 0 ? allKeywords.join(',') : null;

        const res = await fetch(`${API_URL}/saved-searches`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: subscribeName.trim(),
            keywords: keywordsString,
            location: currentFilters.location || [],
            work_field: currentFilters.workField || [],
            job_type: currentFilters.jobType || [],
            work_time: currentFilters.workTime || [],
            salary_min: currentFilters.salaryMin || null,
            company_activity: currentFilters.companyActivity || null,
            is_subscribed: true,
            notification_frequency: subscribeFrequency,
          }),
        });

        if (res.ok) {
          setSubscribeSuccess(true);
          setTimeout(() => { setShowSubscribeModal(false); setSubscribeSuccess(false); }, 2000);
        } else {
          showToast('Nepavyko sukurti prenumeratos', 'error');
        }
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      showToast('Įvyko klaida kuriant prenumeratą', 'error');
    } finally {
      setSubscribing(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
    {/* Toast Notification */}
    {toast.show && (
      <div className={`toast-notification toast-${toast.type}`}>
        {toast.type === 'success' && <CheckCircle size={18} />}
        {toast.type === 'error' && <AlertCircle size={18} />}
        {toast.type === 'info' && <AlertCircle size={18} />}
        <span>{toast.message}</span>
      </div>
    )}
    <div className="job-search-filters">
      <div className="filters-header">
        <h3>PASIRINKTI KRITERIJAI</h3>
        <div className="filters-header-actions">
          <button className="clear-filters-icon" onClick={clearAllFilters} title="Išvalyti filtrus">
            <Trash2 size={18} />
          </button>
          {onCollapse && (
            <button className="collapse-filters-btn" onClick={onCollapse} title="Slėpti filtrus">
              <ChevronLeft size={18} />
            </button>
          )}
        </div>
      </div>


      {/* SELECTED FILTERS - Grouped by section with + button */}
      {getActiveFiltersCount() > 0 && (
        <div className="selected-filters">
          {stagedFilters.location.length > 0 && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Darbo vietos</span>
                <div className="filter-group-pills">
                  {stagedFilters.location.map((loc) => {
                    const count = facets['location']?.[loc] ?? 0;
                    return (
                      <button key={`location-${loc}`} className="filter-pill" onClick={() => removeFilter('location', loc)}>
                        <span>{loc}{count > 0 ? ` (${count})` : ''}</span>
                        <X size={14} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <button className="filter-group-add" onClick={() => openFilterModal('location')} title="Pridėti miestą">
                <PlusCircle size={22} />
              </button>
            </div>
          )}

          {(stagedFilters.fieldIds.length > 0 || stagedFilters.tipasIds.length > 0 || stagedFilters.positionIds.length > 0) && (() => {
            const fieldName = (id: number) => taxFields.find(f => f.id === id)?.name || `#${id}`;
            const tipasName = (id: number) => taxTipai.find(t => t.id === id)?.name || `#${id}`;
            const posName = (id: number) => {
              if (positionNames[id]) return positionNames[id];
              for (const arr of Object.values(positionsByField)) { const m = arr.find(p => p.id === id); if (m) return m.name; }
              return `#${id}`;
            };
            const removeId = (key: 'fieldIds' | 'tipasIds' | 'positionIds', id: number) =>
              updateStagedFilters({ ...stagedFilters, [key]: (stagedFilters[key] as number[]).filter(v => v !== id) });
            return (
              <div className="filter-group">
                <div className="filter-group-content">
                  <span className="filter-group-label">Darbo sritys</span>
                  <div className="filter-group-pills">
                    {stagedFilters.fieldIds.map(id => (
                      <button key={`f-${id}`} className="filter-pill" onClick={() => removeId('fieldIds', id)}><span>{fieldName(id)}</span><X size={14} /></button>
                    ))}
                    {stagedFilters.tipasIds.map(id => (
                      <button key={`t-${id}`} className="filter-pill" onClick={() => removeId('tipasIds', id)}><span>{tipasName(id)}</span><X size={14} /></button>
                    ))}
                    {stagedFilters.positionIds.map(id => (
                      <button key={`p-${id}`} className="filter-pill" onClick={() => removeId('positionIds', id)}><span>{posName(id)}</span><X size={14} /></button>
                    ))}
                  </div>
                </div>
                <button className="filter-group-add" onClick={() => openFilterModal('workField')} title="Pridėti">
                  <PlusCircle size={22} />
                </button>
              </div>
            );
          })()}

          {/* Legacy string-based work fields (e.g. from older saved searches /
              prenumeratos that predate the taxonomy migration). Shown so the
              applied filter is visible and removable. */}
          {stagedFilters.workField.length > 0 && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Darbo sritys</span>
                <div className="filter-group-pills">
                  {stagedFilters.workField.map((field) => (
                    <button key={`workField-${field}`} className="filter-pill" onClick={() => removeFilter('workField', field)}>
                      <span>{field}</span>
                      <X size={14} />
                    </button>
                  ))}
                </div>
              </div>
              <button className="filter-group-add" onClick={() => openFilterModal('workField')} title="Pridėti">
                <PlusCircle size={22} />
              </button>
            </div>
          )}

          {stagedFilters.jobType.length > 0 && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Darbo tipas</span>
                <div className="filter-group-pills">
                  {stagedFilters.jobType.map((type) => {
                    const count = facets['job_type']?.[type] ?? 0;
                    return (
                      <button key={`jobType-${type}`} className="filter-pill" onClick={() => removeFilter('jobType', type)}>
                        <span>{type}{count > 0 ? ` (${count})` : ''}</span>
                        <X size={14} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <button className="filter-group-add" onClick={() => openFilterModal('jobType')} title="Pridėti darbo tipą">
                <PlusCircle size={22} />
              </button>
            </div>
          )}

          {stagedFilters.workTime.length > 0 && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Darbo laikas</span>
                <div className="filter-group-pills">
                  {stagedFilters.workTime.map((time) => {
                    const count = facets['work_time']?.[time] ?? 0;
                    return (
                      <button key={`workTime-${time}`} className="filter-pill" onClick={() => removeFilter('workTime', time)}>
                        <span>{time}{count > 0 ? ` (${count})` : ''}</span>
                        <X size={14} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <button className="filter-group-add" onClick={() => openFilterModal('workTime')} title="Pridėti darbo laiką">
                <PlusCircle size={22} />
              </button>
            </div>
          )}

          {(stagedFilters.companyActivity || stagedFilters.companyNames.length > 0) && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Įmonė</span>
                <div className="filter-group-pills">
                  {stagedFilters.companyActivity && (
                    <button className="filter-pill" onClick={() => updateStagedFilters({ ...stagedFilters, companyActivity: '' })}>
                      <span>{stagedFilters.companyActivity}</span>
                      <X size={14} />
                    </button>
                  )}
                  {stagedFilters.companyNames.map((name) => (
                    <button key={`company-${name}`} className="filter-pill" onClick={() => removeFilter('companyNames' as any, name)}>
                      <span>{name}</span>
                      <X size={14} />
                    </button>
                  ))}
                </div>
              </div>
              <button className="filter-group-add" onClick={() => openFilterModal('companies')} title="Pridėti įmonę">
                <PlusCircle size={22} />
              </button>
            </div>
          )}

          {stagedFilters.salaryMin && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Atlyginimas</span>
                <div className="filter-group-pills">
                  <button className="filter-pill" onClick={() => updateStagedFilters({ ...stagedFilters, salaryMin: '' })}>
                    <span>Nuo {stagedFilters.salaryMin} €</span>
                    <X size={14} />
                  </button>
                </div>
              </div>
              <button className="filter-group-add" onClick={() => openFilterModal('salary')} title="Keisti atlyginimą">
                <PlusCircle size={22} />
              </button>
            </div>
          )}

          {stagedFilters.language.length > 0 && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Kalbos</span>
                <div className="filter-group-pills">
                  {stagedFilters.language.map((lang) => (
                    <button key={`language-${lang}`} className="filter-pill" onClick={() => removeFilter('language', lang)}>
                      <span>{lang}</span>
                      <X size={14} />
                    </button>
                  ))}
                </div>
              </div>
              <button className="filter-group-add" onClick={() => openFilterModal('postParams')} title="Pridėti kalbą">
                <PlusCircle size={22} />
              </button>
            </div>
          )}

          {stagedFilters.keywords.length > 0 && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Raktažodžiai</span>
                <div className="filter-group-pills">
                  {stagedFilters.keywords.map((keyword) => (
                    <button key={`keyword-${keyword}`} className="filter-pill" onClick={() => removeKeyword(keyword)}>
                      <span>{keyword}</span>
                      <X size={14} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {stagedFilters.postedWithin && stagedFilters.postedWithin !== '' && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Skelbimo data</span>
                <div className="filter-group-pills">
                  <button className="filter-pill" onClick={() => updateStagedFilters({ ...stagedFilters, postedWithin: '' })}>
                    <span>{{
                      '0': 'Šiandien',
                      '3': 'Per 3 dienas',
                      '7': 'Per savaitę',
                      '14': 'Per 2 savaites',
                      '-14': 'Senesni 2 sav.',
                    }[stagedFilters.postedWithin] || stagedFilters.postedWithin}</span>
                    <X size={14} />
                  </button>
                </div>
              </div>
              <button className="filter-group-add" onClick={() => openFilterModal('postParams')} title="Keisti parametrus">
                <PlusCircle size={22} />
              </button>
            </div>
          )}

          {stagedFilters.directEmployersOnly && (
            <div className="filter-group">
              <div className="filter-group-content">
                <span className="filter-group-label">Papildomi parametrai</span>
                <div className="filter-group-pills">
                  <button className="filter-pill" onClick={() => updateStagedFilters({ ...stagedFilters, directEmployersOnly: false })}>
                    <span>Tiesioginiai darbdaviai</span>
                    <X size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ACTION BUTTONS - Right after selected filters */}
      <div className="filter-actions">
        <button className="action-btn save-btn" onClick={handleSaveSearch}>
          <Download size={18} />
          Išsaugoti
        </button>
        <button className="action-btn subscribe-btn" onClick={handleSubscribe}>
          <Mail size={18} />
          Prenumeruoti
        </button>
      </div>

      {/* SAVE SEARCH PANEL */}
      {showSavePanel && (
        <div className="save-search-panel">
          <div className="save-panel-header">
            <SlidersHorizontal size={18} />
            <span>Nustatymai</span>
            <button className="save-panel-close" onClick={() => setShowSavePanel(false)}>
              <X size={16} />
            </button>
          </div>
          <div className="save-panel-body">
            <label className="save-panel-label">Paieškos pavadinimas</label>
            <input
              type="text"
              className="save-panel-input"
              placeholder="Įrašykite"
              value={saveSearchTitle}
              onChange={(e) => setSaveSearchTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && confirmSaveSearch()}
              autoFocus
            />
            <button className="save-panel-btn" onClick={confirmSaveSearch}>
              <Save size={16} />
              Išsaugoti paiešką
            </button>
          </div>
        </div>
      )}

      {/* DARBO VIETOS (Location) */}
      <div className="filter-section">
        <button 
          className="section-header-btn"
          onClick={() => openFilterModal('location')}
        >
          <div className="section-title">
            <span className="section-icon-wrap">
              <MapPin size={24} className="section-icon" />
              {stagedFilters.location.length > 0 && <span className="filter-badge">{stagedFilters.location.length}</span>}
            </span>
            <span>DARBO VIETOS</span>
          </div>
          <span className="expand-icon">+</span>
        </button>
      </div>

      {/* DARBO SRITYS (Work Fields + Position Types + Positions - cascading) */}
      <div className="filter-section">
        <button 
          className="section-header-btn"
          onClick={() => openFilterModal('workField')}
        >
          <div className="section-title">
            <span className="section-icon-wrap">
              <Briefcase size={24} className="section-icon" />
              {(stagedFilters.fieldIds.length + stagedFilters.tipasIds.length + stagedFilters.positionIds.length + stagedFilters.workField.length) > 0 && <span className="filter-badge">{stagedFilters.fieldIds.length + stagedFilters.tipasIds.length + stagedFilters.positionIds.length + stagedFilters.workField.length}</span>}
            </span>
            <span>DARBO SRITYS</span>
          </div>
          <span className="expand-icon">+</span>
        </button>
      </div>

      {/* ATLYGINIMAS (Salary) */}
      <div className="filter-section">
        <button 
          className="section-header-btn"
          onClick={() => openFilterModal('salary')}
        >
          <div className="section-title">
            <span className="section-icon-wrap">
              <Euro size={24} className="section-icon" />
              {stagedFilters.salaryMin && <span className="filter-badge">1</span>}
            </span>
            <span>ATLYGINIMAS</span>
          </div>
          <span className="expand-icon">+</span>
        </button>
      </div>

      {/* ĮMONĖS (Companies + Activity Sectors) */}
      <div className="filter-section">
        <button 
          className="section-header-btn"
          onClick={() => openFilterModal('companies')}
        >
          <div className="section-title">
            <span className="section-icon-wrap">
              <Building size={24} className="section-icon" />
              {((stagedFilters.companyActivity ? 1 : 0) + stagedFilters.companyNames.length) > 0 && <span className="filter-badge">{(stagedFilters.companyActivity ? 1 : 0) + stagedFilters.companyNames.length}</span>}
            </span>
            <span>ĮMONĖS</span>
          </div>
          <span className="expand-icon">+</span>
        </button>
      </div>

      {/* DARBO TIPAS (Job Type) */}
      <div className="filter-section">
        <button 
          className="section-header-btn"
          onClick={() => openFilterModal('jobType')}
        >
          <div className="section-title">
            <span className="section-icon-wrap">
              <Clock size={24} className="section-icon" />
              {stagedFilters.jobType.length > 0 && <span className="filter-badge">{stagedFilters.jobType.length}</span>}
            </span>
            <span>DARBO TIPAS</span>
          </div>
          <span className="expand-icon">+</span>
        </button>
      </div>

      {/* DARBO LAIKAS (Work Time) */}
      <div className="filter-section">
        <button 
          className="section-header-btn"
          onClick={() => openFilterModal('workTime')}
        >
          <div className="section-title">
            <span className="section-icon-wrap">
              <Calendar size={24} className="section-icon" />
              {stagedFilters.workTime.length > 0 && <span className="filter-badge">{stagedFilters.workTime.length}</span>}
            </span>
            <span>DARBO LAIKAS</span>
          </div>
          <span className="expand-icon">+</span>
        </button>
      </div>

      {/* SKELBIMO PARAMETRAI (Posting Parameters) */}
      <div className="filter-section">
        <button 
          className="section-header-btn"
          onClick={() => openFilterModal('postParams')}
        >
          <div className="section-title">
            <span className="section-icon-wrap">
              <Settings size={24} className="section-icon" />
              {(stagedFilters.language.length + (stagedFilters.postedWithin ? 1 : 0) + (stagedFilters.directEmployersOnly ? 1 : 0)) > 0 && <span className="filter-badge">{stagedFilters.language.length + (stagedFilters.postedWithin ? 1 : 0) + (stagedFilters.directEmployersOnly ? 1 : 0)}</span>}
            </span>
            <span>SKELBIMO PARAMETRAI</span>
          </div>
          <span className="expand-icon">+</span>
        </button>
      </div>
    </div>

      {/* Modals */}
      <FilterModal
        isOpen={openModal === 'location'}
        onClose={closeModal}
        title="Darbo vietos"
        icon={<MapPin size={20} />}
        resultCount={liveCount}
        countLoading={countLoading}
        onShowResults={applyFilters}
        onClearAll={() => { setLocationSearch(''); updateStagedFilters({ ...stagedFilters, location: [] }); }}
      >
        <input
          type="text"
          placeholder="Įveskite miestą"
          value={locationSearch}
          onChange={(e) => setLocationSearch(e.target.value)}
          className="filter-input"
        />
        {(() => {
          const allLocations = (cvTemplates.work_location || LITHUANIAN_CITIES.map((city, index) => ({ 
            id: index, 
            category: 'work_location', 
            value: city, 
            label: city 
          }))).filter(loc => 
            !locationSearch || 
            loc.value.toLowerCase().includes(locationSearch.toLowerCase())
          );
          const { major: topCities, rest: restCities } = splitMajorCities(allLocations);

          const renderCity = (loc: any) => {
            const count = facets['location']?.[loc.value] ?? 0;
            const isDisabled = count === 0 && hasFacets;
            return (
              <label
                key={loc.id}
                className="city-checkbox-label"
                style={{
                  opacity: isDisabled ? 0.4 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                  cursor: isDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={stagedFilters.location.includes(loc.value)}
                  onChange={() => handleArrayCheckbox('location', loc.value)}
                />
                <span className="filter-option-label">{loc.value}</span>
                {count > 0 && (
                  <span className="filter-option-count">
                    {count.toLocaleString('lt-LT')}
                  </span>
                )}
              </label>
            );
          };

          return (
            <>
              {topCities.length > 0 && (
                <div className="city-top5">
                  {topCities.map(renderCity)}
                </div>
              )}
              {topCities.length > 0 && restCities.length > 0 && (
                <hr className="city-top5-divider" />
              )}
              {restCities.length > 0 && (
                <div className="cities-grid">
                  {restCities.map(renderCity)}
                </div>
              )}
            </>
          );
        })()}
      </FilterModal>

      {/* DARBO SRITYS + PAREIGYBIŲ TIPAI + PAREIGYBĖS - Cascading modal */}
      <FilterModal
        isOpen={openModal === 'workField'}
        onClose={closeModal}
        title="Darbo sritys"
        icon={<Briefcase size={20} />}
        resultCount={liveCount}
        countLoading={countLoading}
        onShowResults={applyFilters}
        onClearAll={() => updateStagedFilters({ ...stagedFilters, fieldIds: [], tipasIds: [], positionIds: [] })}
      >
        {(() => {
          const disabled = (count: number, selected: boolean) => taxFacetsReady && count === 0 && !selected;
          const selFields = stagedFilters.fieldIds;
          const selTipai = stagedFilters.tipasIds;
          const selPos = stagedFilters.positionIds;
          const toggle = (key: 'fieldIds' | 'tipasIds' | 'positionIds', id: number) => {
            const cur = stagedFilters[key] as number[];
            const next = cur.includes(id) ? cur.filter(v => v !== id) : [...cur, id];
            updateStagedFilters({ ...stagedFilters, [key]: next });
          };
          return (
            <div className="work-field-layout">
              <div className="wf-card work-field-col">
                <h4><Briefcase size={16} /> Darbo sritys</h4>
                <input
                  type="text"
                  placeholder="Įveskite darbo sritį"
                  value={taxFieldSearch}
                  onChange={(e) => setTaxFieldSearch(e.target.value)}
                  className="filter-input"
                />
                <div className="checkbox-list wf-two-col">
                  {taxFields
                    .filter(f => !taxFieldSearch || f.name.toLowerCase().includes(taxFieldSearch.toLowerCase()))
                    .map(f => {
                      const checked = selFields.includes(f.id);
                      const count = taxFacets.fields[f.id] ?? 0;
                      const isDisabled = disabled(count, checked);
                      return (
                        <label key={f.id} className="checkbox-label" style={{ opacity: isDisabled ? 0.4 : 1, pointerEvents: isDisabled ? 'none' : 'auto', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                          <input type="checkbox" checked={checked} onChange={() => { toggle('fieldIds', f.id); ensurePositionsForField(f.id); }} />
                          <span className="filter-option-label">{f.name}</span>
                          {taxFacetsReady && count > 0 && <span className="filter-option-count">{count.toLocaleString('lt-LT')}</span>}
                        </label>
                      );
                    })}
                </div>
              </div>
              <div className="work-field-right">
                <div className="wf-card position-type-col">
                  <h4><Briefcase size={16} /> Pareigybių tipai</h4>
                  <div className="checkbox-list position-type-grid">
                    {taxTipai.map(t => {
                      const checked = selTipai.includes(t.id);
                      const count = taxFacets.tipai[t.id] ?? 0;
                      const isDisabled = disabled(count, checked);
                      return (
                        <label key={t.id} className="checkbox-label" style={{ opacity: isDisabled ? 0.4 : 1, pointerEvents: isDisabled ? 'none' : 'auto', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggle('tipasIds', t.id)} />
                          <span className="filter-option-label">{t.name}</span>
                          {taxFacetsReady && count > 0 && <span className="filter-option-count">{count.toLocaleString('lt-LT')}</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="wf-card positions-col">
                  <h4><Briefcase size={16} /> Pareigybės pagal pasirinktas darbo sritis</h4>
                  {selFields.length === 0 ? (
                    <div className="no-results-hint">Pasirinkite bent vieną darbo sritį</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="Įveskite pareigybę"
                        value={taxPosSearch}
                        onChange={(e) => setTaxPosSearch(e.target.value)}
                        className="filter-input"
                      />
                      <div className="checkbox-list">
                        {(() => {
                          const seen = new Set<number>();
                          const positions: { id: number; name: string; tipas_id?: number | null }[] = [];
                          for (const fid of selFields) {
                            for (const p of positionsByField[fid] || []) {
                              if (seen.has(p.id)) continue;
                              seen.add(p.id);
                              // Narrow by the chosen Pareigybių tipai (if any).
                              if (selTipai.length > 0 && !(p.tipas_id != null && selTipai.includes(p.tipas_id))) continue;
                              if (!taxPosSearch || p.name.toLowerCase().includes(taxPosSearch.toLowerCase())) positions.push(p);
                            }
                          }
                          positions.sort((a, b) => a.name.localeCompare(b.name, 'lt'));
                          if (positions.length === 0) return <div className="no-results-hint">Pareigybių nerasta</div>;
                          return positions.map(p => {
                            const count = taxFacets.positions[p.id] ?? 0;
                            const isDisabled = disabled(count, selPos.includes(p.id));
                            return (
                              <label key={p.id} className="checkbox-label" style={{ opacity: isDisabled ? 0.4 : 1, pointerEvents: isDisabled ? 'none' : 'auto', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                                <input type="checkbox" checked={selPos.includes(p.id)} onChange={() => toggle('positionIds', p.id)} />
                                <span className="filter-option-label">{p.name}</span>
                                {taxFacetsReady && count > 0 && <span className="filter-option-count">{count.toLocaleString('lt-LT')}</span>}
                              </label>
                            );
                          });
                        })()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </FilterModal>

      {/* DARBO TIPAS - Single column */}
      <FilterModal
        isOpen={openModal === 'jobType'}
        onClose={closeModal}
        title="Darbo tipas"
        icon={<Clock size={20} />}
        resultCount={liveCount}
        countLoading={countLoading}
        onShowResults={applyFilters}
        onClearAll={() => updateStagedFilters({ ...stagedFilters, jobType: [] })}
      >
        <div className="checkbox-list single-column">
          {cvTemplates.work_type?.map((type) => {
            const count = facets['job_type']?.[type.value] ?? 0;
            const isDisabled = count === 0 && hasFacets;
            return (
              <label
                key={type.id}
                className="checkbox-label"
                style={{
                  opacity: isDisabled ? 0.4 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                  cursor: isDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={stagedFilters.jobType.includes(type.value)}
                  onChange={() => handleArrayCheckbox('jobType', type.value)}
                />
                <span className="filter-option-label">{type.value}</span>
                {count > 0 && (
                  <span className="filter-option-count">
                    {count.toLocaleString('lt-LT')}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </FilterModal>

      {/* DARBO LAIKAS - Single column */}
      <FilterModal
        isOpen={openModal === 'workTime'}
        onClose={closeModal}
        title="Darbo laikas"
        icon={<Calendar size={20} />}
        resultCount={liveCount}
        countLoading={countLoading}
        onShowResults={applyFilters}
        onClearAll={() => updateStagedFilters({ ...stagedFilters, workTime: [] })}
      >
        <div className="checkbox-list single-column">
          {cvTemplates.work_time?.map((time) => {
            const count = facets['work_time']?.[time.value] ?? 0;
            const isDisabled = count === 0 && hasFacets;
            return (
              <label
                key={time.id}
                className="checkbox-label"
                style={{
                  opacity: isDisabled ? 0.4 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                  cursor: isDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={stagedFilters.workTime.includes(time.value)}
                  onChange={() => handleArrayCheckbox('workTime', time.value)}
                />
                <span className="filter-option-label">{time.value}</span>
                {count > 0 && (
                  <span className="filter-option-count">
                    {count.toLocaleString('lt-LT')}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </FilterModal>

      <FilterModal
        isOpen={openModal === 'salary'}
        onClose={closeModal}
        title="Atlyginimas"
        icon={<Euro size={20} />}
        resultCount={liveCount}
        countLoading={countLoading}
        onShowResults={applyFilters}
        onClearAll={() => { setSalarySlider(0); updateStagedFilters({ ...stagedFilters, salaryMin: '' }); }}
      >
        <div className="salary-slider-wrap">
          <div className="salary-slider-label">
            {salarySlider === 0
              ? 'Bet koks atlyginimas'
              : `Nuo ${salarySlider.toLocaleString('lt-LT')} €`}
          </div>
          <input
            type="range"
            min={0}
            max={50000}
            step={50}
            value={salarySlider}
            style={{ '--slider-pct': `${(salarySlider / 50000) * 100}%` } as React.CSSProperties}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setSalarySlider(val);
              if (salaryDebounceRef.current) clearTimeout(salaryDebounceRef.current);
              salaryDebounceRef.current = setTimeout(() => {
                handleInputChange('salaryMin', val === 0 ? '' : String(val));
              }, 300);
            }}
          />
          <div className="salary-slider-range">
            <span>0 €</span>
            <span>50 000 €</span>
          </div>
        </div>
      </FilterModal>

      {/* ĮMONĖS - Combined: activity sectors (left) + companies (right) */}
      <FilterModal
        isOpen={openModal === 'companies'}
        onClose={closeModal}
        title="Įmonės"
        icon={<Building size={20} />}
        resultCount={liveCount}
        countLoading={countLoading}
        onShowResults={applyFilters}
        onClearAll={() => { setCompanySearch(''); updateStagedFilters({ ...stagedFilters, companyActivity: '', companyNames: [] }); }}
      >
        <div className="two-column-layout">
          <div className="filter-column">
            <h4>Įmonių veiklos sritys</h4>
            <div className="checkbox-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {(() => {
                const sectorMap = new Map<string, number>();
                companies.forEach(c => {
                  const sector = c.company_activity;
                  if (sector && sector.trim()) {
                    sectorMap.set(sector, (sectorMap.get(sector) || 0) + c.job_count);
                  }
                });
                const sectors = Array.from(sectorMap.entries())
                  .sort((a, b) => b[1] - a[1]);

                return sectors.map(([sector]) => {
                  const count = facets['company_activity']?.[sector] ?? 0;
                  const isDisabled = count === 0 && hasFacets;
                  return (
                    <label
                      key={sector}
                      className="checkbox-label"
                      style={{
                        opacity: isDisabled ? 0.4 : 1,
                        pointerEvents: isDisabled ? 'none' : 'auto',
                        cursor: isDisabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={stagedFilters.companyActivity === sector}
                        onChange={() => handleInputChange('companyActivity',
                          stagedFilters.companyActivity === sector ? '' : sector)}
                      />
                      <span className="filter-option-label">{sector}</span>
                      {count > 0 && (
                        <span className="filter-option-count">
                          {count.toLocaleString('lt-LT')}
                        </span>
                      )}
                    </label>
                  );
                });
              })()}
            </div>
          </div>
          <div className="filter-column">
            <h4>Įmonės</h4>
            <input
              type="text"
              placeholder="Įveskite įmonės pavadinimą"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="filter-input"
            />
            <div className="checkbox-list" style={{ maxHeight: '360px', overflowY: 'auto' }}>
              {(() => {
                const sectorFilter = stagedFilters.companyActivity;
                const filtered = companies.filter(c => {
                  if (sectorFilter && (c.company_activity || '') !== sectorFilter) {
                    return false;
                  }
                  if (companySearch && !c.company_name.toLowerCase().includes(companySearch.toLowerCase())) {
                    return false;
                  }
                  return true;
                });

                if (sectorFilter && filtered.length === 0) {
                  return (
                    <div className="no-results-hint">
                      Pasirinktoje veiklos srityje įmonių nerasta
                    </div>
                  );
                }

                return filtered.map((company) => (
                  <label key={`company-${company.company_name}`} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={stagedFilters.companyNames.includes(company.company_name)}
                      onChange={() => handleArrayCheckbox('companyNames', company.company_name)}
                    />
                    <span>{company.company_name} ({company.job_count})</span>
                  </label>
                ));
              })()}
            </div>
          </div>
        </div>
      </FilterModal>

      {/* SKELBIMO PARAMETRAI - Two column layout */}
      <FilterModal
        isOpen={openModal === 'postParams'}
        onClose={closeModal}
        title="Skelbimo parametrai"
        icon={<Settings size={20} />}
        resultCount={liveCount}
        countLoading={countLoading}
        onShowResults={applyFilters}
        onClearAll={() => updateStagedFilters({ ...stagedFilters, language: [], postedWithin: '' })}
      >
        <div className="modal-two-columns">
          {/* Left column - Language & Employer Type */}
          <div className="modal-column">
            <h4 className="modal-section-title">Skelbimo kalba</h4>
            <div className="language-options">
              {(cvTemplates.job_language || [
                { id: 1, category: 'job_language', value: 'Lietuvių', label: 'Lietuvių' },
                { id: 2, category: 'job_language', value: 'Anglų', label: 'Anglų' },
                { id: 3, category: 'job_language', value: 'Rusų', label: 'Rusų' }
              ]).map((lang) => (
                <label key={lang.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={stagedFilters.language.includes(lang.value)}
                    onChange={() => handleArrayCheckbox('language', lang.value)}
                  />
                  <span>{lang.value}</span>
                </label>
              ))}
            </div>

          </div>

          {/* Right column - Posted Within */}
          <div className="modal-column">
            <h4 className="modal-section-title">Paskelbimo laikas</h4>
            <div className="radio-list">
              <label className="radio-label">
                <input
                  type="radio"
                  name="postedWithin"
                  value="0"
                  checked={stagedFilters.postedWithin === '0'}
                  onChange={(e) => handleInputChange('postedWithin', e.target.value)}
                />
                <span>Šiandien</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="postedWithin"
                  value="3"
                  checked={stagedFilters.postedWithin === '3'}
                  onChange={(e) => handleInputChange('postedWithin', e.target.value)}
                />
                <span>Per paskutines 3 dienas</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="postedWithin"
                  value="7"
                  checked={stagedFilters.postedWithin === '7'}
                  onChange={(e) => handleInputChange('postedWithin', e.target.value)}
                />
                <span>Per paskutinę savaitę</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="postedWithin"
                  value="14"
                  checked={stagedFilters.postedWithin === '14'}
                  onChange={(e) => handleInputChange('postedWithin', e.target.value)}
                />
                <span>Per paskutines dvi savaites</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="postedWithin"
                  value="-14"
                  checked={stagedFilters.postedWithin === '-14'}
                  onChange={(e) => handleInputChange('postedWithin', e.target.value)}
                />
                <span>Senesni negu dvi savaitės</span>
              </label>
            </div>
          </div>
        </div>
      </FilterModal>

      {/* Subscribe Modal */}
      {showSubscribeModal && createPortal(
        <div className="subscribe-modal-overlay" onClick={() => { if (!subscribeSuccess) { setShowSubscribeModal(false); } }}>
          <div className="subscribe-modal" onClick={(e) => e.stopPropagation()}>
            {subscribeSuccess ? (
              <div className="subscribe-modal-success">
                <CheckCircle size={48} />
                <h3>Prenumerata sukurta!</h3>
                <p>Pranešimus gausite el. paštu.</p>
              </div>
            ) : (
              <>
                <div className="subscribe-modal-header">
                  <h3>Prenumeruoti paiešką</h3>
                  <button className="btn-close-modal" onClick={() => setShowSubscribeModal(false)}>
                    <X size={20} />
                  </button>
                </div>
                <div className="subscribe-modal-body">
                  <div className="form-group">
                    <label>Paieškos pavadinimas *</label>
                    <input
                      type="text"
                      value={subscribeName}
                      onChange={(e) => setSubscribeName(e.target.value)}
                      placeholder="pvz., Programuotojas Vilniuje"
                    />
                  </div>
                  {!isAuthenticated && (
                    <div className="form-group">
                      <label>El. paštas *</label>
                      <input
                        type="email"
                        value={subscribeEmail}
                        onChange={(e) => setSubscribeEmail(e.target.value)}
                        placeholder="jusu@pastas.lt"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Siuntimo dažnis</label>
                    <div className="frequency-pills">
                      <button
                        type="button"
                        className={`frequency-pill ${subscribeFrequency === 'daily' ? 'active' : ''}`}
                        onClick={() => setSubscribeFrequency('daily')}
                      >
                        Kas dieną
                      </button>
                      <button
                        type="button"
                        className={`frequency-pill ${subscribeFrequency === 'weekly' ? 'active' : ''}`}
                        onClick={() => setSubscribeFrequency('weekly')}
                      >
                        Kas savaitę
                      </button>
                    </div>
                  </div>
                </div>
                <div className="subscribe-modal-footer">
                  <button className="btn-secondary" onClick={() => setShowSubscribeModal(false)}>
                    Atšaukti
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSubscribeSubmit}
                    disabled={subscribing}
                  >
                    {subscribing ? 'Siunčiama...' : 'PRENUMERUOTI'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default JobSearchFilters;
